import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Grid,
    CircularProgress,
    IconButton,
    Paper,
    Divider,
    Switch,
    FormControlLabel,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Product } from '../types';
import {
    startPromptGeneration,
    getGenerationStatus,
    startImageGeneration,
    getProductById,
    getGenerationsForProduct,
} from '../services/api';
import MarketplaceSelectionDialog from '../components/MarketplaceSelectionDialog';
import ImagePreviewModal from '../components/product/ImagePreviewModal';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ImageGenerationViewPageProps {
    product?: Product | null;
    generation?: any;
    onGenerationComplete?: (newGeneration: any) => void;
}

const ImageGenerationViewPage: React.FC<ImageGenerationViewPageProps> = ({
                                                                             product,
                                                                             generation,
                                                                             onGenerationComplete,
                                                                         }) => {
    const navigate = useNavigate();
    const params = useParams();
    const { t } = useTranslation();

    const routeProductId = Number(params.productId ?? params.id ?? 0) || 0;
    const productId = product?.id ?? routeProductId;
    const generationId = Number(params.generationId ?? 0);

    const [loadedProduct, setLoadedProduct] = useState<Product | null>(product ?? null);
    useEffect(() => {
        if (!product && productId) {
            (async () => {
                try {
                    const data = await getProductById(productId);
                    setLoadedProduct(data);
                } catch (err) {
                    console.error(t('imageGeneration.failed_to_fetch_product'), err);
                }
            })();
        }
    }, [product, productId, t]);

    const [photoCount, setPhotoCount] = useState(1);
    const [userPrompt, setUserPrompt] = useState('');
    const [status, setStatus] = useState('');
    const [generatedImages, setGeneratedImages] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [marketplaceDialogOpen, setMarketplaceDialogOpen] = useState(false);
    const [selectedMarketplace, setSelectedMarketplace] = useState<{ id: number; name: string } | null>(null);
    const [selectedReferenceImage, setSelectedReferenceImage] = useState<string | null>(null);
    const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [showOnlyOriginals, setShowOnlyOriginals] = useState(true);
    const [previousGeneratedImages, setPreviousGeneratedImages] = useState<string[]>([]);

    useEffect(() => {
        if (generationId && productId) {
            (async () => {
                try {
                    const data = await getGenerationStatus(productId, generationId);
                    setGeneratedImages(data.image_requests ?? []);
                    setStatus(data.status ?? '');
                } catch (err) {
                    console.error(t('imageGeneration.failed_to_load_generation'), err);
                }
            })();
        }
    }, [generationId, productId, t]);

    useEffect(() => {
        if (productId) {
            (async () => {
                try {
                    const generations = await getGenerationsForProduct(productId);
                    const allPrevImages: string[] = [];
                    generations.forEach(gen => {
                        gen.image_requests?.forEach((req: any) => {
                            if (req.generated_image_url) allPrevImages.push(req.generated_image_url);
                        });
                    });
                    setPreviousGeneratedImages(allPrevImages);
                } catch (err) {
                    console.error(t('imageGeneration.failed_to_fetch_previous_generations'), err);
                }
            })();
        }
    }, [productId, t]);

    useEffect(() => {
        if (generation) {
            if (generation.image_requests) setGeneratedImages(generation.image_requests);
            if (generation.status) setStatus(generation.status);
        }
    }, [generation]);

    const selectedPromptTemplateId = 29;

    const handleGenerate = async () => {
        setLoading(true);
        setStatus(t('imageGeneration.starting_generation'));
        setGeneratedImages([]);

        try {
            const payload: any = {
                prompt_template_id: selectedPromptTemplateId,
                ai_service_used: 'google_gemini-2.5-flash',
                image_ai_service_used: 'google_gemini-2.5-flash_image_preview',
                num_images: photoCount,
                extra_instructions: userPrompt,
                reference_image_url: selectedReferenceImage || '',
                marketplace_id: selectedMarketplace?.id ?? null,
            };

            const startResult = await startPromptGeneration(productId, payload);
            const generationId = startResult.id;

            setStatus(t('imageGeneration.waiting_for_prompts'));

            let generationResult: any = null;
            for (let i = 0; i < 60; i++) {
                try {
                    generationResult = await getGenerationStatus(productId, generationId);
                } catch (err) {
                    generationResult = null;
                }

                if (generationResult?.status) setStatus(generationResult.status);
                if (generationResult?.status === 'prompts_ready') break;
                await new Promise(r => setTimeout(r, 3000));
            }

            if (!generationResult || generationResult.status !== 'prompts_ready') {
                setStatus(t('imageGeneration.failed_to_prepare_prompts'));
                setLoading(false);
                return;
            }

            setStatus(t('imageGeneration.starting_image_generation'));
            try {
                await startImageGeneration(productId, generationId);
            } catch (err) {
                setStatus(t('imageGeneration.error_starting_image_generation'));
                setLoading(false);
                return;
            }

            for (let i = 0; i < 60; i++) {
                try {
                    generationResult = await getGenerationStatus(productId, generationId);
                } catch (err) {
                    generationResult = null;
                }

                if (generationResult?.status) setStatus(generationResult.status);

                if (generationResult?.image_requests?.length) {
                    setGeneratedImages(prev => {
                        const prevIds = new Set(prev.map(img => img.id));
                        const newImages = generationResult.image_requests.filter((img: any) => !prevIds.has(img.id));
                        return [...prev, ...newImages];
                    });
                }

                if (generationResult?.status === 'completed' || generationResult?.status === 'failed') break;

                await new Promise(r => setTimeout(r, 3000));
            }

            setStatus(generationResult?.status || t('imageGeneration.completed'));
            if (onGenerationComplete) onGenerationComplete(generationResult);
        } catch (err) {
            setStatus(t('imageGeneration.error_generating_images'));
        } finally {
            setLoading(false);
        }
    };

    const downloadImage = async (url: string, filename?: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename || 'image.jpg';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error(t('imageGeneration.failed_to_download_image'), err);
        }
    };

    const downloadAllImages = async () => {
        if (generatedImages.length === 0) return;
        const zip = new JSZip();
        const folder = zip.folder('images');
        for (let i = 0; i < generatedImages.length; i++) {
            const img = generatedImages[i];
            if (img.generated_image_url) {
                try {
                    const response = await fetch(img.generated_image_url);
                    const blob = await response.blob();
                    folder?.file(`image_${i + 1}.jpg`, blob);
                } catch (err) {
                    console.error(t('imageGeneration.failed_to_fetch_image_for_zip'), err);
                }
            }
        }
        const content = await zip.generateAsync({ type: 'blob' });
        saveAs(content, 'generated_images.zip');
    };

    return (
        <Box sx={{ p: 3, width: '100%', mx: 'auto' }}>
            {/* Верхняя панель */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton onClick={() => navigate(`/dashboard/catalog/${productId}`)}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" sx={{ ml: 1, fontWeight: 600 }}>
                    {t('imageGeneration.image_generation_for')}{' '}
                    <Box component="span" sx={{ color: 'orange', fontStyle: 'italic' }}>
                        “{loadedProduct?.name ?? `#${productId}`}”
                    </Box>
                </Typography>
            </Box>

            <Paper sx={{ p: 3 }}>
                {/* Верхняя панель настроек */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2.5,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        mb: 2,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={() => setMarketplaceDialogOpen(true)}
                        sx={{ minWidth: 150, minHeight: 56, alignSelf: 'stretch', flexShrink: 0 }}
                    >
                        {selectedMarketplace ? selectedMarketplace.name : t('imageGeneration.select_marketplace')}
                    </Button>
                    <TextField
                        type="number"
                        label={t('imageGeneration.number_of_photos')}
                        value={photoCount}
                        onChange={(e) => setPhotoCount(Number(e.target.value))}
                        sx={{ minWidth: 150, minHeight: 56, alignSelf: 'stretch', flexShrink: 0 }}
                        inputProps={{ min: 1, max: 10 }}
                        size="medium"
                    />
                    <TextField
                        label={t('imageGeneration.user_prompt_optional')}
                        value={userPrompt}
                        onChange={(e) => setUserPrompt(e.target.value.slice(0, 200))}
                        placeholder={t('imageGeneration.prompt_placeholder')}
                        sx={{ flexGrow: 1, minWidth: 150, minHeight: 56, alignSelf: 'stretch' }}
                        inputProps={{ maxLength: 200 }}
                        size="medium"
                    />
                    <Button
                        variant="contained"
                        onClick={handleGenerate}
                        disabled={loading || Boolean(generationId)}
                        sx={{ minWidth: 140, minHeight: 56, alignSelf: 'stretch', flexShrink: 0 }}
                    >
                        {loading ? <CircularProgress size={20} /> : generationId ? t('imageGeneration.view_only') : t('imageGeneration.generate')}
                    </Button>
                </Box>

                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: { xs: 1, sm: 0 } }}>
                        {t('imageGeneration.reference_image')}
                    </Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showOnlyOriginals}
                                onChange={() => setShowOnlyOriginals(!showOnlyOriginals)}
                                color="primary"
                                size="small"
                            />
                        }
                        label={t('imageGeneration.only_originals')}
                        labelPlacement="start"
                        sx={{ ml: 0, mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'flex-start',
                        overflowX: 'auto',
                        whiteSpace: 'nowrap',
                        pb: 1,
                        mb: 3,
                    }}
                >
                    {loadedProduct?.images?.map((img, i) => (
                        <Box
                            key={`orig-${i}`}
                            sx={{
                                width: 80,
                                height: 80,
                                border: selectedReferenceImage === img ? '2px solid orange' : '1px solid #ccc',
                                borderRadius: 1,
                                cursor: 'pointer',
                                overflow: 'hidden',
                                flex: '0 0 auto',
                            }}
                            onClick={() => setSelectedReferenceImage(img)}
                        >
                            <img src={img} alt={`orig-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                    ))}

                    {!showOnlyOriginals && previousGeneratedImages.map((url, i) => (
                        <Box
                            key={`prev-${i}`}
                            sx={{
                                width: 80,
                                height: 80,
                                border: selectedReferenceImage === url ? '2px solid orange' : '1px solid #ccc',
                                borderRadius: 1,
                                cursor: 'pointer',
                                overflow: 'hidden',
                                flex: '0 0 auto',
                            }}
                            onClick={() => setSelectedReferenceImage(url)}
                        >
                            <img src={url} alt={`prev-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                    ))}

                    {!showOnlyOriginals && generatedImages.map((imgReq, i) => (
                        imgReq.generated_image_url && (
                            <Box
                                key={`gen-${i}`}
                                sx={{
                                    width: 80,
                                    height: 80,
                                    border: selectedReferenceImage === imgReq.generated_image_url ? '2px solid orange' : '1px solid #ccc',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    overflow: 'hidden',
                                    flex: '0 0 auto',
                                }}
                                onClick={() => setSelectedReferenceImage(imgReq.generated_image_url)}
                            >
                                <img src={imgReq.generated_image_url} alt={`gen-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </Box>
                        )
                    ))}
                </Box>
                <Divider sx={{ mb: 2 }} />

                {status && (
                    <Typography
                        variant="body2"
                        color={status === 'completed' ? 'success.main' : status === 'failed' ? 'error.main' : 'text.secondary'}
                        sx={{ mb: 2 }}
                    >
                        {t('imageGeneration.status')}: {status}
                    </Typography>
                )}

                <Grid container spacing={2}>
                    {generatedImages.map((imgReq, i) => (
                        <Grid item key={i} xs={12} sm={6} md={4} lg={3}>
                            <Box
                                sx={{
                                    width: '100%',
                                    aspectRatio: '16/9',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    overflow: 'hidden',
                                    boxShadow: 1,
                                    position: 'relative',
                                }}
                                onClick={() => {
                                    setPreviewImageUrl(imgReq.generated_image_url);
                                    setIsPreviewOpen(true);
                                }}
                            >
                                {imgReq.generated_image_url ? (
                                    <>
                                        <img
                                            src={imgReq.generated_image_url}
                                            alt={`generated-${i}`}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                        <IconButton
                                            size="small"
                                            sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.7)' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                downloadImage(imgReq.generated_image_url, `generated_${i + 1}.jpg`);
                                            }}
                                        >
                                            <DownloadIcon fontSize="small" />
                                        </IconButton>
                                    </>
                                ) : (
                                    <Box
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <CircularProgress />
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                {generatedImages.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={downloadAllImages}
                        >
                            {t('imageGeneration.download_all')}
                        </Button>
                    </Box>
                )}
            </Paper>

            <MarketplaceSelectionDialog
                open={marketplaceDialogOpen}
                onClose={() => setMarketplaceDialogOpen(false)}
                onSelect={(id) => {
                    const chosen = { id, name: `Marketplace #${id}` };
                    setSelectedMarketplace(chosen);
                    setMarketplaceDialogOpen(false);
                }}
                productName={loadedProduct?.name ?? `#${productId}`}
            />

            <ImagePreviewModal
                imageUrl={previewImageUrl || ''}
                open={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
            />
        </Box>
    );
};

export default ImageGenerationViewPage;
